import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { CardView } from "@breeze/shared";
import { STAGE_ACCENT } from "../theme.js";
import { SLATicker } from "./SLATicker.js";
import { usePresenceStore } from "../hooks/usePresence.js";
import { useSelection } from "../store/selection.js";
import { useIsDesktop } from "../hooks/useIsDesktop.js";

type Props = {
  card: CardView;
  onOpen?: (cardId: number) => void;
  isOverlay?: boolean;
  currentUser?: string;
};

type AgeBadge = { text: string; tone: "warn" | "alert" } | null;

// Build the two-line preview of the newest message on a card.
// Emails: line 1 = subject, line 2 = first non-empty body line.
// SMS/WA/VoIP: line 1 + line 2 = first two non-empty body lines.
// Each line is truncated with an ellipsis if it exceeds CHAR_LIMIT.
const CHAR_LIMIT = 44;
function truncate(s: string): string {
  const clean = s.replace(/\s+/g, " ").trim();
  return clean.length > CHAR_LIMIT ? clean.slice(0, CHAR_LIMIT - 3) + "..." : clean;
}
// Collapse all whitespace runs (incl. line breaks) into single spaces
// for the card preview ONLY. Card display has limited vertical space —
// if a customer's email starts with "Hi there,\n\nThank you, how much
// is...", we don't want preview line 2 to waste itself on "Hi there,".
// Collapsing lets the truncate() pack in more useful text per line.
// The slide-out Timeline keeps the original line breaks via its own
// rendering — this is card-display only.
function collapseToOneLine(s: string | null): string {
  if (!s) return "";
  return s.replace(/\s+/g, " ").trim();
}

function buildMessagePreview(card: CardView): { line1: string; line2: string } | null {
  const body = card.lastMessageBody;
  const subject = card.lastMessageSubject;
  const ch = card.lastMessageChannel;
  if (!body && !subject) return null;
  const collapsed = collapseToOneLine(body);
  if (ch === "email") {
    const line1 = truncate(subject ?? "");
    const line2 = truncate(collapsed);
    if (!line1 && !line2) return null;
    return { line1, line2 };
  }
  // SMS / WA / VOIP: no subject — wrap the collapsed body across both
  // preview lines. Break at the nearest word boundary near CHAR_LIMIT
  // so long messages still use the full preview area instead of being
  // cut off after the first line.
  if (collapsed.length <= CHAR_LIMIT) {
    return { line1: collapsed, line2: "" };
  }
  let breakAt = CHAR_LIMIT;
  const space = collapsed.lastIndexOf(" ", CHAR_LIMIT);
  if (space > CHAR_LIMIT * 0.6) breakAt = space;
  const line1 = collapsed.slice(0, breakAt).trim();
  const line2 = truncate(collapsed.slice(breakAt));
  if (!line1 && !line2) return null;
  return { line1, line2 };
}

function ageBadge(iso: string | null): AgeBadge {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  const days = Math.floor(ms / 86_400_000);
  if (days < 7) return null;
  if (days < 14) return { text: `${days}d`, tone: "warn" };
  return { text: `${days}d`, tone: "alert" };
}

export function Card({ card, onOpen, isOverlay = false, currentUser }: Props): JSX.Element {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: card.cardId });

  const accent = STAGE_ACCENT[card.stage];
  const archived = card.archivedAt !== null;
  const age = ageBadge(card.lastActivityAt ?? card.createdAt);

  // Selection state (multi-select for bulk actions). Desktop-only —
  // mobile/touch doesn't have hover so bulk selection UX doesn't fit.
  const isDesktop = useIsDesktop();
  const isSelected = useSelection((s) => s.selected.has(card.cardId));
  const anySelected = useSelection((s) => s.selected.size > 0) && isDesktop;
  const toggleSelection = useSelection((s) => s.toggle);

  // Presence: other users viewing this card right now.
  const viewers = usePresenceStore((s) => s.byCard[card.cardId] ?? []);
  const otherViewers = currentUser
    ? viewers.filter((v) => v.email.toLowerCase() !== currentUser.toLowerCase())
    : viewers;
  const presenceColor = otherViewers[0]?.color ?? null;

  // Attention signal: the single yellow flash covers every "this card needs
  // someone on the team" trigger. Read-state is TEAM-SHARED — when anyone
  // opens the card, the flash clears for everyone; "Mark unread" wipes it
  // for the whole team so the card pulses again (typically paired with an
  // assignee change to escalate). Flash fires on:
  //   - an inbound message newer than lastReadAt (outbound sends never
  //     flash — staff just sent them, gated by lastMessageDirection === 'in')
  //   - a pending AI draft newer than lastReadAt (Emma needs approval)
  //   - a non-null flaggedAt (manual Mark-Unread by staff). Covers cards
  //     with no inbound message where the time-based comparison has
  //     nothing to beat.
  const lastReadMs = card.lastReadAt
    ? new Date(card.lastReadAt).getTime()
    : 0;
  const hasUnreadInbound =
    card.lastMessageAt !== null &&
    card.lastMessageDirection === "in" &&
    new Date(card.lastMessageAt).getTime() > lastReadMs;
  const pendingAt = card.pendingAction?.createdAt ?? null;
  const hasUnreadPending =
    pendingAt !== null && new Date(pendingAt).getTime() > lastReadMs;
  const isManuallyFlagged = card.flaggedAt !== null;
  const needsAttention =
    !isOverlay && (hasUnreadInbound || hasUnreadPending || isManuallyFlagged);

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging && !isOverlay ? 0.3 : archived ? 0.6 : 1,
    borderLeftColor: accent,
    ...(presenceColor
      ? {
          outline: `2px solid ${presenceColor}`,
          outlineOffset: "-2px",
        }
      : {}),
  };

  const name = card.accountName ?? card.contactName ?? `Card #${card.cardId}`;
  const costLabel =
    card.totalCost !== null && card.totalCost > 0
      ? `£${card.totalCost.toFixed(2)}`
      : null;

  // WON cards double as a schedule view — show "Wed 30 Apr · 10:00 · Tom"
  // when the card has a SCHEDULEDATE. Unscheduled bookings stay in WON
  // too (see pipelineRules.shouldStayInWon) so the date may be absent.
  const scheduleLabel: string | null = (() => {
    if (card.stage !== "won" || !card.scheduleDate) return null;
    const d = new Date(card.scheduleDate + "T00:00:00");
    if (Number.isNaN(d.getTime())) return null;
    const day = d.toLocaleDateString("en-GB", { weekday: "short" });
    const dateStr = d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
    const time = card.scheduleStartTime ?? null;
    const tech = card.technicianNickname ?? null;
    return [
      `${day} ${dateStr}`,
      time,
      tech,
    ].filter(Boolean).join(" · ");
  })();
  // SLA ticker tells us when the customer last messaged. Lost is the only
  // truly terminal stage. WON keeps it: a booked customer can still ping
  // before or on the day (e.g. cancellation, parking, time change), and
  // staff need to spot that fast.
  const showSla = card.stage !== "lost";

  // Two-line preview of the latest message: subject + first-body-line for
  // email, first two body lines for SMS/WhatsApp/VoIP.
  const preview = buildMessagePreview(card);

  const handleClick = (e: React.MouseEvent): void => {
    if (isDragging) return;
    // While in selection mode, clicks toggle selection rather than open.
    if (anySelected) {
      e.stopPropagation();
      toggleSelection(card.cardId);
      return;
    }
    if (onOpen) {
      e.stopPropagation();
      onOpen(card.cardId);
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={{ ...style, position: "relative" }}
      {...attributes}
      {...listeners}
      onClick={handleClick}
      className={`group mb-2 cursor-grab rounded-md border border-ink-600 border-l-[3px] bg-ink-700 px-3 py-2.5 text-ink-100 shadow-card transition-all duration-150 hover:-translate-y-[1px] hover:border-ink-500 hover:bg-ink-600 hover:shadow-cardHover active:cursor-grabbing ${
        isOverlay ? "shadow-drag ring-1 ring-slack-aubergineLight/40" : ""
      } ${needsAttention ? "card-unread" : ""} ${
        isSelected ? "ring-2 ring-slack-aubergineLight" : ""
      }`}
    >
      {!isOverlay ? (
        <button
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            toggleSelection(card.cardId);
          }}
          className={`absolute bottom-1 right-1 hidden h-4 w-4 items-center justify-center rounded-sm border text-[9px] leading-none transition-opacity md:flex ${
            isSelected
              ? "border-slack-aubergineLight bg-slack-aubergine text-ink-50 opacity-100"
              : `border-ink-500 bg-ink-800/80 text-transparent hover:border-slack-aubergineLight ${
                  anySelected ? "opacity-80" : "opacity-0 group-hover:opacity-80"
                }`
          }`}
          title={isSelected ? "Deselect" : "Select"}
          aria-pressed={isSelected}
        >
          ✓
        </button>
      ) : null}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1 truncate text-[14px] font-semibold leading-snug text-ink-50">
          {name}
          {card.displayedIdentityFromAi ? (
            <span
              className="ml-1 inline-block text-[12px] align-top"
              title="This identity was extracted by the AI"
            >
              ✨
            </span>
          ) : null}
        </div>
        {card.postcode ? (
          <span
            className="shrink-0 rounded-sm border border-ink-500 bg-ink-800/70 px-1.5 py-0.5 font-mono text-[12px] font-semibold uppercase tracking-[0.08em] text-ink-50"
          >
            {card.postcode}
          </span>
        ) : null}
      </div>
      {costLabel ? (
        <div className="mt-1.5 text-[12px] font-semibold tabular-nums text-ink-50">
          {costLabel}
        </div>
      ) : null}
      {scheduleLabel ? (
        <div className="mt-1 inline-flex items-center gap-1.5 rounded-sm border border-slack-green/40 bg-slack-green/10 px-1.5 py-0.5 text-[12px] font-semibold tabular-nums text-slack-green">
          <span aria-hidden>📅</span>
          {scheduleLabel}
        </div>
      ) : null}
      {preview ? (
        <div className="mt-1 text-[12px] leading-snug text-ink-200">
          <div className="truncate">{preview.line1}</div>
          <div className="truncate">{preview.line2}</div>
        </div>
      ) : null}
      <div className="mt-1.5 flex items-center gap-2 text-[12px] text-ink-50">
        {showSla ? <SLATicker card={card} /> : null}
        <div className="ml-auto flex items-center gap-1.5">
          {age ? (
            <span
              className="rounded-sm border border-ink-500 bg-ink-900/60 px-1.5 py-0.5 font-mono text-[11px] font-semibold uppercase tracking-wider text-ink-50"
              title={`Inactive for ${age.text}`}
            >
              {age.text} stale
            </span>
          ) : null}
          {archived ? (
            <span
              className="rounded-sm border border-ink-500 bg-ink-900/60 px-1.5 py-0.5 font-mono text-[11px] font-semibold uppercase tracking-wider text-ink-50"
              title={`Archived ${new Date(card.archivedAt as string).toLocaleDateString()}`}
            >
              Arch
            </span>
          ) : null}
          {card.priority === 1 ? (
            <span
              className="h-2 w-2 rounded-full bg-slack-yellow"
              title="priority"
            />
          ) : null}
          {card.assignedUser ? (
            <span
              className="flex h-6 w-6 items-center justify-center rounded-full bg-slack-aubergine text-[12px] font-semibold uppercase text-ink-50"
              title={card.assignedUser}
            >
              {card.assignedUser.slice(0, 1)}
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}
